import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DossierSettlement } from '../../api/dossier';
import { missingInExact, money, same } from './money';
import './Money.scss';

interface Segment {
    key: string;
    label: string;
    amount: number;
    tone: 'paid' | 'reduced' | 'open' | 'gone';
}

const positive = (n?: number | null) => (n && n > 0.004 ? n : 0);

/** How Chargebee sees the invoice: what was paid, made smaller, written off, and what is open. */
const chargebeeSegments = (s: DossierSettlement): Segment[] =>
    [
        { key: 'paid', label: 'betaald', amount: positive(s.paidInChargebee), tone: 'paid' as const },
        {
            key: 'reduced',
            label: 'creditnota / aanpassing',
            amount: positive((s.adjustedInChargebee ?? 0) + (s.creditedInChargebee ?? 0)),
            tone: 'reduced' as const,
        },
        { key: 'gone', label: 'afgeschreven', amount: positive(s.writtenOffInChargebee), tone: 'gone' as const },
        { key: 'open', label: 'open', amount: positive(s.openInChargebee), tone: 'open' as const },
    ].filter((segment) => segment.amount > 0);

/** How Exact sees it: what was matched away, and what is still open. */
const exactSegments = (s: DossierSettlement, total: number): Segment[] => {
    const open = positive(s.openInExact);

    return [
        { key: 'matched', label: 'afgeletterd', amount: positive(total - open), tone: 'paid' as const },
        { key: 'open', label: 'open', amount: open, tone: 'open' as const },
    ].filter((segment) => segment.amount > 0);
};

/**
 * The invoice in Chargebee and in Exact as two bars on the same scale: what is settled, how, and what is
 * open. A difference between the two shows as bars that do not line up, before anyone reads a number.
 */
export const BalanceBars = ({ settlement, hideVerdict }: { settlement: DossierSettlement; hideVerdict?: boolean }) => {
    const total = settlement.total ?? 0;
    const chargebeeRead = settlement.openInChargebee !== null && settlement.openInChargebee !== undefined;
    const exactRead = settlement.openInExact !== null && settlement.openInExact !== undefined;
    const agree = chargebeeRead && exactRead && same(settlement.openInChargebee, settlement.openInExact);
    const difference = chargebeeRead && exactRead ? (settlement.openInExact ?? 0) - (settlement.openInChargebee ?? 0) : null;

    return (
        <div className="balance" aria-label={`Stand van factuur ${settlement.invoiceId}`}>
            <Bar system="Chargebee" total={total} segments={chargebeeRead ? chargebeeSegments(settlement) : []} read={chargebeeRead} open={settlement.openInChargebee} />
            {missingInExact(settlement) ? (
                <div className="balance__row">
                    <span className="balance__system">Exact</span>
                    <div className="balance__track balance__track--missing">
                        <span className="balance__unread">
                            <FontAwesomeIcon icon="triangle-exclamation" /> staat niet in Exact
                        </span>
                    </div>
                    <span className="balance__open" />
                </div>
            ) : (
                <Bar system="Exact" total={total} segments={exactRead ? exactSegments(settlement, total) : []} read={exactRead} open={settlement.openInExact} />
            )}

            {chargebeeRead && exactRead && !hideVerdict && (
                <p className={`balance__verdict balance__verdict--${agree ? 'ok' : 'warn'}`}>
                    <FontAwesomeIcon icon={agree ? 'circle-check' : 'triangle-exclamation'} />{' '}
                    {agree
                        ? `Gelijk: in beide staat ${money(settlement.openInChargebee)} open.`
                        : difference !== null && difference > 0
                          ? `Verschil ${money(difference)}: Exact heeft ${money(settlement.openInExact)} open, Chargebee ${money(settlement.openInChargebee)}.`
                          : `Verschil ${money(Math.abs(difference ?? 0))}: Chargebee heeft ${money(settlement.openInChargebee)} open, Exact ${money(settlement.openInExact)}.`}
                </p>
            )}
        </div>
    );
};

const Bar = ({ system, total, segments, read, open }: { system: string; total: number; segments: Segment[]; read: boolean; open?: number | null }) => (
    <div className="balance__row">
        <span className="balance__system">{system}</span>
        <div className="balance__track" role="img" aria-label={read ? segments.map((s) => `${s.label} ${money(s.amount)}`).join(', ') : `${system} niet gelezen`}>
            {!read && <span className="balance__unread">niet gelezen</span>}
            {read &&
                segments.map((segment) => (
                    <span
                        key={segment.key}
                        className={`balance__part balance__part--${segment.tone}`}
                        style={{ flexGrow: total > 0 ? segment.amount / total : 1 }}
                        title={`${segment.label}: ${money(segment.amount)}`}
                    >
                        <span className="balance__part-label">
                            {segment.label} {money(segment.amount)}
                        </span>
                    </span>
                ))}
            {read && segments.length === 0 && <span className="balance__unread">niets</span>}
        </div>
        <span className={`balance__open${read && (open ?? 0) > 0.004 ? ' balance__open--due' : ''}`}>{read ? `${money(open)} open` : ''}</span>
    </div>
);
